import { isRemoteDeviceAction } from "./remote-device-frame";
import type { WsMessage } from "@/api/websocket";
import { applyBillingLimitEvent } from "@/lib/billing-limit-event";
import {
  applyCompanionFrame,
  type FrameApplyContext,
} from "@/lib/companion-frame-reducer";
import {
  applyAgentPresentationFrame,
  observeAgentPresentationLifecycle,
} from "@/lib/agent-presentation-controller";
import { applyWorkToastFrame } from "@/lib/work-toast-frame";
import { observeLiveWorkToastCreation } from "@/lib/work-toast-live-events";
import { isStaleWorkToastQuestionFrame } from "@/lib/work-toast-question-delivery";

function applyStateFrame(message: WsMessage, context: FrameApplyContext): void {
  if (message.device_local !== false) {
    if (applyBillingLimitEvent(message)) return;
    observeAgentPresentationLifecycle(message);
    if (applyAgentPresentationFrame(message)) return;
  }
  const resolvesQuestion =
    message.type === "question.answer_delivery" &&
    ["pending", "delivered", "unavailable", "superseded"].includes(
      String(message.data.status),
    ) &&
    typeof message.data.awaiting_input_id === "string" &&
    typeof message.data.execution_id === "string";
  applyCompanionFrame(
    resolvesQuestion
      ? { ...message, type: "awaiting_input.resolved" }
      : message,
    context,
  );
}

/**
 * Applies a companion frame at a Tauri window boundary. Visual presentation
 * frames are owned by the cancellable controller; every other frame continues
 * through the shared state reducer. A new work toast is announced only after
 * the whole frame is applied, so the toast gate sees the cursor the same
 * frame creates for its run.
 */
export function applyTauriCompanionFrame(
  message: WsMessage,
  context: FrameApplyContext,
): void {
  if (isRemoteDeviceAction(message)) return;
  if (isStaleWorkToastQuestionFrame(message)) return;
  const workStore = context.workToastStore;
  const workBefore = workStore?.getState().items;
  applyWorkToastFrame(message, context);
  applyStateFrame(message, context);
  if (workBefore && workStore) {
    observeLiveWorkToastCreation(workBefore, workStore.getState().items);
  }
}
